import { useGPFormContext } from '@/contexts/GPFormContext';
import { FormSectionType } from '@/types/forms';
import { isFilterFieldActive } from '@/utils/filterFieldActive';
import { useCallback, useEffect } from 'react';
import { get, set } from 'react-hook-form';

/**
 * Drives a one-click filter section (see {@link isToggleFilterSection}): there is no
 * popover to Apply from, so flipping the chip writes the single switch field and
 * submits straight away.
 */
export const useFilterToggleSection = (section: FormSectionType) => {
  const { defaultValues, getValues, handleSubmit, onSubmit, reset, setModified, valueState } =
    useGPFormContext();

  const name = section.fields[0]?.name ?? '';
  const active = !!name && isFilterFieldActive(valueState, name, defaultValues);

  // Same bookkeeping the popover chips do, so "Clear all" counts this filter.
  useEffect(() => {
    if (!name) return;

    setModified(prev => {
      const next = new Set(prev);
      if (active) {
        next.add(name);
      } else {
        next.delete(name);
      }
      return next;
    });
  }, [active, name, setModified]);

  const toggle = useCallback(() => {
    if (!name) return;

    const newValues = { ...getValues() };
    set(newValues, name, active ? get(defaultValues, name) : 1);
    reset(newValues);
    handleSubmit(onSubmit)();
  }, [active, defaultValues, getValues, handleSubmit, name, onSubmit, reset]);

  return { active, toggle };
};
